import { FC, PropsWithChildren, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Navigate, generatePath } from 'react-router-dom';
import { ABSOLUTE_ROUTES } from 'configs/routes';
import { useAppBrand, useAuth, useHandleErrors } from 'hooks';
import useLogos from 'hooks/useLogos';
import { FORM_VALIDATORS } from 'utils/FormValidators';
import styles from './Login.module.css';
import cariSideImage from './cari.svg';
import dukanSideImage from './dukan.svg';
import { Button, Col, Form, Input, Row, Space } from 'antd';
import { LockOutlined, UserOutlined } from '@ant-design/icons';

const LoginLayout: FC<PropsWithChildren> = ({ children }) => {
	const { fullLogo } = useLogos();
	const currentYear = new Date().getFullYear();
	const { brand, isCari } = useAppBrand();

	const { t: tLogin } = useTranslation('login');

	// ! render
	return (
		<div className={styles.layout}>
			<div className={styles.logo_container}>
				<img
					alt='logo'
					loading='lazy'
					src={fullLogo}
					className={styles.logo}
				/>
			</div>
			{children}
			<div className={styles.footer_container}>
				<div className={styles.copyright}>
					{tLogin('copyright', { currentYear, brand: tLogin(`brands.${brand}`) })}
				</div>
				{isCari && <div className={styles.footer_logo_container} />}
				<div className={styles.footer_background_container} />
			</div>
		</div>
	);
};

const LoginPage: FC = () => {
	const auth = useAuth();
	const { handleError } = useHandleErrors();
	const { isCari, isDukan } = useAppBrand();
	const { t: tLogin } = useTranslation('login');
	const { t: tCommon } = useTranslation('common');

	// ! states
	const [signinLoading, setSigninLoading] = useState(false);

	// ! handlers
	const handleLogin = ({ email = '', password = '' }) => {
		setSigninLoading(true);
		auth
			//
			.signin(email, password)
			.catch((e) => {
				handleError(e, true);
				setSigninLoading(false);
			});
	};

	// ! render
	if (auth.isAuthenticated()) {
		return <Navigate to={generatePath(ABSOLUTE_ROUTES.DASHBOARD)} />;
	}

	return (
		<Row
			wrap
			gutter={[124, 124]}
		>
			<Col
				xs={0}
				sm={0}
				md={0}
				lg={12}
			>
				<div className='flex flex-center'>
					{isCari && (
						<img
							src={cariSideImage}
							alt='login-icon'
							width={400}
							className={styles.img}
						/>
					)}
					{isDukan && (
						<img
							src={dukanSideImage}
							alt='login-icon'
							width={400}
							className={styles.img}
						/>
					)}
				</div>
			</Col>
			<Col>
				<Form
					layout='vertical'
					name='auth_form'
					className={styles.auth_form}
					validateTrigger='onBlur'
					onFinish={handleLogin}
				>
					<Form.Item>
						<Space
							direction='vertical'
							size={10}
						>
							<h2 className={styles.title}>{tLogin('title')}</h2>
						</Space>
					</Form.Item>
					<Form.Item
						name='email'
						label={tLogin('form.email.label')}
						rules={[
							{ ...FORM_VALIDATORS.REQUIRED(), message: tLogin('form_validations.email.required') },
							{ type: 'email', message: 'Please input a valid email.' },
						]}
					>
						<Input
							size='large'
							autoComplete='off'
							placeholder={tLogin('form.email.placeholder')}
							prefix={<UserOutlined />}
						/>
					</Form.Item>
					<Form.Item
						name='password'
						label={tLogin('form.password.placeholder')}
						rules={[
							{ ...FORM_VALIDATORS.REQUIRED(), message: tLogin('form_validations.password.required') },
						]}
					>
						<Input.Password
							size='large'
							autoComplete='off'
							placeholder={tLogin('form.password.placeholder')}
							prefix={<LockOutlined className='site-form-item-icon' />}
						/>
					</Form.Item>

					<Form.Item>
						<Button
							block
							size='large'
							type='primary'
							htmlType='submit'
							loading={signinLoading}
							disabled={signinLoading}
						>
							{tCommon('action_buttons.login')}
						</Button>
					</Form.Item>
				</Form>
			</Col>
		</Row>
	);
};

export const Login: FC = () => (
	<LoginLayout>
		<LoginPage />
	</LoginLayout>
);
